/**
 * PrizeEventRow — one event inside an expanded rodeo on the Prize Management tab.
 *
 * Three nested levels, each paying for itself only when opened:
 *   collapsed  → renders from the rodeo summary already in hand. No fetch.
 *   expanded   → useEventPrizeDetail: allocation panel + contestant list.
 *   Edit prizes→ the SAME RodeoEventDialog My Rodeos uses, saved through the
 *                SAME persistence path (features/prize-management/eventConfigPersistence).
 *                Its supporting queries (rounds, performances, junctions) are
 *                gated on the dialog actually being open.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Trophy, Pencil, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/contexts/ToastContext';
import {
  RodeoEventDialog,
  type RodeoEventDialogValues,
} from '@/components/rodeo-wizard/RodeoEventDialog';
import { PrizeAllocationPanel } from './PrizeAllocationPanel';
import { PrizeContestantList } from './PrizeContestantList';
import {
  EventPrizeDisplayToggles,
  type EventPrizeDisplayTogglesValues,
} from '@/components/delegate/EventPrizeDisplayToggles';
import { useSaveEventPrizeToggles } from '@/features/prize-management/prizeDisplayToggles';
import { useImpersonateUserFlow } from '@/hooks/useImpersonateUserFlow';
import {
  useEventPrizeDetail,
  useSetPrizePoolExclusion,
  prizeOverviewKeys,
  type EventPrizeSummary,
  type PrizeOverviewEntry,
} from '@/api/extensions/usePrizeOverview';
import {
  buildEventDialogInitialValues,
  useSaveRodeoEventConfig,
} from '@/features/prize-management/eventConfigPersistence';
import { useRoundConfigsByEvent } from '@/hooks/useRoundConfigsByEvent';
import { useEvents } from '@/api/generated_hooks/generated-events-hooks';
import {
  usePerformanceEventsClient,
  performanceEventsKeys,
} from '@/api/generated_hooks/generated-performance_events-hooks';
import {
  usePerformancesClient,
  performancesKeys,
} from '@/api/generated_hooks/generated-performances-hooks';

const money = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export interface PrizeEventRowProps {
  summary: EventPrizeSummary;
  rodeoId: string;
  /** Enables the editor's inherited org-default prize adjustments. */
  organizationId: string | null;
}

export function PrizeEventRow({ summary, rodeoId, organizationId }: PrizeEventRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const contestantsRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { saveEventConfig } = useSaveRodeoEventConfig();
  const { saveEventToggles } = useSaveEventPrizeToggles();
  const {
    impersonate,
    canImpersonate,
    currentUserProfileId,
  } = useImpersonateUserFlow();
  const [impersonatePendingId, setImpersonatePendingId] = useState<string | null>(null);

  const eventId = summary.event_id;
  const { breakdown } = summary;

  // Level 2 — only when this event row is open.
  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useEventPrizeDetail(eventId, { enabled: expanded });

  const exclusionMutation = useSetPrizePoolExclusion();

  // ── Editor's supporting data. All gated on the dialog being open. ──
  const { data: fullEvent } = useEvents(dialogOpen ? eventId : '');
  const { data: existingRounds } = useRoundConfigsByEvent(
    dialogOpen ? eventId : undefined,
  );

  const performanceEventsClient = usePerformanceEventsClient();
  const performancesClient = usePerformancesClient();

  const { data: existingJoins } = useQuery({
    queryKey: performanceEventsKeys.list({ event_id: eventId }),
    queryFn: async () => {
      const res = await performanceEventsClient.getAll({
        filters: { event_id: eventId },
      } as any);
      return Array.isArray(res) ? res : ((res as any)?.data ?? []);
    },
    enabled: dialogOpen,
  });

  const { data: rodeoPerfs } = useQuery({
    queryKey: performancesKeys.list({ trig_rodeo_id: rodeoId }),
    queryFn: async () => {
      const res = await performancesClient.getAll({
        filters: { trig_rodeo_id: rodeoId },
      } as any);
      return Array.isArray(res) ? res : ((res as any)?.data ?? []);
    },
    enabled: dialogOpen,
  });

  const initialValues = useMemo(
    () =>
      fullEvent
        ? buildEventDialogInitialValues(fullEvent, existingRounds, existingJoins)
        : undefined,
    [fullEvent, existingRounds, existingJoins],
  );

  const performanceOptions = useMemo(
    () =>
      (rodeoPerfs ?? []).map((p: any) => {
        const start = p.start_datetime ? new Date(p.start_datetime) : null;
        return {
          key: p.id,
          name: p.performance_name ?? 'Performance',
          performance_date: start ? start.toISOString().slice(0, 10) : null,
          start_time: start ? start.toISOString().slice(11, 19) : null,
        };
      }),
    [rodeoPerfs],
  );

  const fallbackPerformanceId =
    (rodeoPerfs ?? []).length > 0 ? (rodeoPerfs as any[])[0].id : null;

  const handleSubmit = async (values: RodeoEventDialogValues) => {
    if (!fullEvent) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await saveEventConfig({
        event: fullEvent,
        rodeoId,
        values,
        existingRounds: existingRounds as any,
        existingJoins: existingJoins as any,
        fallbackPerformanceId,
      });
      // The prize config just moved, so every number on this page is stale.
      queryClient.invalidateQueries({ queryKey: prizeOverviewKeys.all });
      setDialogOpen(false);
      showSuccess('Prize settings saved');
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Prize-display toggles (Contestants / Results / Live) ─────────────────
  // Same three switches as My Rodeos, driven from the summary the overview
  // already shipped, so no extra events fetch. Optimistic with revert — a
  // failed write must spring the switch back, not sit there lying.
  const summaryToggles: EventPrizeDisplayTogglesValues = useMemo(
    () => ({
      display_prize_to_contestants: summary.display_prize_to_contestants,
      show_prize_amounts_in_results: summary.show_prize_amounts_in_results,
      show_prize_amounts_before_complete: summary.show_prize_amounts_before_complete,
    }),
    [summary],
  );
  const [toggleValues, setToggleValues] =
    useState<EventPrizeDisplayTogglesValues>(summaryToggles);
  const [togglesSaving, setTogglesSaving] = useState(false);

  // Re-sync when the overview refetches (e.g. after the rodeo master toggle
  // writes this event, or the edit dialog saves).
  useEffect(() => {
    setToggleValues(summaryToggles);
  }, [summaryToggles]);

  const handleTogglesChange = async (next: EventPrizeDisplayTogglesValues) => {
    const prev = toggleValues;
    setToggleValues(next);
    setTogglesSaving(true);
    try {
      await saveEventToggles(eventId, next);
      queryClient.invalidateQueries({ queryKey: prizeOverviewKeys.all });
    } catch (err) {
      setToggleValues(prev);
      showError(
        'Could not save toggle',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setTogglesSaving(false);
    }
  };

  const handleImpersonate = async (entry: PrizeOverviewEntry) => {
    if (!entry.user_profile_id) return;
    setImpersonatePendingId(entry.registration_id);
    try {
      await impersonate(entry.user_profile_id);
    } finally {
      setImpersonatePendingId(null);
    }
  };

  const handleToggleExclusion = async (
    entry: PrizeOverviewEntry,
    excluded: boolean,
  ) => {
    try {
      await exclusionMutation.mutateAsync({
        registrationId: entry.registration_id,
        excluded,
      });
      showSuccess(
        excluded
          ? `${entry.contestant_name} removed from the prize pool`
          : `${entry.contestant_name} added back to the prize pool`,
      );
    } catch (err) {
      showError(
        'Could not update the prize pool',
        err instanceof Error ? err.message : 'Please try again.',
      );
    }
  };

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-background">
      <div className="flex items-center gap-2 border-l-2 border-border bg-muted/40 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              expanded ? 'rotate-90' : ''
            }`}
            aria-hidden="true"
          />
          <Trophy className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium">{summary.event_name}</span>
              <span className="text-xs text-muted-foreground">
                {breakdown.allocation_mode === 'dynamic' ? 'Dynamic' : 'Fixed'}
              </span>
              {breakdown.needs_review_count > 0 && (
                <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {breakdown.needs_review_count} to review
                </span>
              )}
            </span>
            {/* Collapsed summary — the three figures, in words as well as colour. */}
            <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
              <span className="text-emerald-700 dark:text-emerald-400">
                {money(breakdown.locked_amount)} locked
              </span>
              <span className="text-amber-700 dark:text-amber-400">
                {money(breakdown.potential_amount)} potential
              </span>
              <span className="text-red-700 dark:text-red-400">
                {money(breakdown.excluded_amount)} not included
              </span>
              <span className="text-muted-foreground">
                {breakdown.locked_count + breakdown.potential_count} in pool
              </span>
            </span>
          </span>
        </button>

        {/* Not nested inside the disclosure button — flipping a switch must
            not also expand the row. */}
        <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <EventPrizeDisplayToggles
            idPrefix={`prize-mgmt-evt-${eventId}`}
            values={toggleValues}
            onChange={handleTogglesChange}
            disabled={togglesSaving}
          />
        </span>

        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={() => setDialogOpen(true)}
        >
          <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Edit prizes
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4 p-3">
          {isLoading && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Loading prize breakdown…
            </p>
          )}

          {isError && (
            <p className="py-4 text-center text-sm text-red-600 dark:text-red-400">
              {error instanceof Error
                ? error.message
                : 'Could not load this event’s prize breakdown.'}
            </p>
          )}

          {detail && (
            <>
              <PrizeAllocationPanel
                breakdown={detail.breakdown}
                math={detail.math}
                onReviewFlagged={() =>
                  contestantsRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              />

              {detail.prize.prize_rounds.length > 0 && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold">Payout by round</h4>
                  <div className="space-y-1.5">
                    {detail.prize.prize_rounds.map((round) => (
                      <div
                        key={`${round.round_number}-${round.round_name}`}
                        className="rounded-md border border-border/60 px-3 py-2"
                      >
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">
                            {round.round_name ||
                              (round.is_average
                                ? 'Average'
                                : `Round ${round.round_number}`)}
                          </span>
                          <span className="font-semibold tabular-nums">
                            ${round.total.toLocaleString()}
                          </span>
                        </div>
                        {round.places.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            {round.places.map((p) => (
                              <span key={p.place} className="tabular-nums">
                                {p.place}. ${p.amount.toLocaleString()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div ref={contestantsRef}>
                <h4 className="mb-2 text-xs font-semibold">
                  Contestants ({detail.entries.length})
                </h4>
                <PrizeContestantList
                  entries={detail.entries}
                  onToggleExclusion={handleToggleExclusion}
                  isTogglePending={exclusionMutation.isPending}
                  canImpersonate={canImpersonate}
                  currentUserProfileId={currentUserProfileId}
                  onImpersonate={handleImpersonate}
                  impersonatePendingId={impersonatePendingId}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/*
        The prize editor itself — not a copy of it. Same component, same values
        shape, same save path as My Rodeos → event → pencil.
      */}
      {dialogOpen && initialValues && (
        <RodeoEventDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setSaveError(null);
          }}
          mode="edit"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          performances={performanceOptions}
          errorMessage={saveError}
          isSaving={isSaving}
          showRounds
          organizationId={organizationId}
          // The button says "Edit prizes" — land on Prizes, not Event Details.
          // The other two steps stay reachable via the left nav.
          initialStep={3}
        />
      )}
    </div>
  );
}
